/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Note } from '@vsolv/packages/notes/domain';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { InfoCardConfig } from '@vsolv/vectors-ui/info-card';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { SecurityService } from '@wsphere/staff/web';
import { Claim } from '@wsphere/warranties/domain';
import { combineLatest, firstValueFrom, map, switchMap, take } from 'rxjs';
import { ClaimNoteDialog, ViewClaimDocumentDialog } from '../../dialogs';
import { ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-notes',
  templateUrl: './claim-notes.page.html',
  standalone: false,
})
export class ClaimNotesPage implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private linkSvc: LinksWebService,
    private securitySvc: SecurityService
  ) {}

  @ViewChild('noteDialog') noteDialog?: ClaimNoteDialog;
  @ViewChild('documentDialog') documentDialog?: ViewClaimDocumentDialog;

  open = Claim.Status.OPEN;
  notes: Note.Model[] = [];
  selectedNote: Note.Model | null = null;
  private linksByNoteId = new Map<string, { link: Link.Model; config: InfoCardConfig }[]>();

  readonly claim$ = this.claimSvc.getClaim();

  canViewNotes$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_ViewNote', permissionKey);
    })
  );

  canManageNotes$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_ManageNote', permissionKey);
    })
  );

  canCreateNotes$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_CreateNote', permissionKey);
    })
  );

  routedNote$ = this.route.queryParams.pipe(
    map(params => {
      const routeNoteId = params?.['noteId'];
      console.log('routeNoteId', routeNoteId);
      if (routeNoteId) {
        setTimeout(() => {
          this.selectedNote = this.notes.find(note => note.id === routeNoteId) ?? null;

          if (this.selectedNote) {
            this.openNoteDialog(this.selectedNote);
          }

          this.router.navigate(['.'], { relativeTo: this.route });
        }, 100);
      }
    })
  );

  noteAndLinks$ = combineLatest([this.claim$, this.claimSvc.getClaimNotes()]).pipe(
    switchMap(async ([claim, notes]) => {
      if (!claim || !notes) return [];

      this.notes = notes;

      // Drop cached links for notes that no longer exist
      const noteIds = new Set(notes.map(note => note.id));
      Array.from(this.linksByNoteId.keys()).forEach(id => {
        if (!noteIds.has(id)) this.linksByNoteId.delete(id);
      });

      const missingNotes = notes.filter(note => !this.linksByNoteId.has(note.id));

      await Promise.all(
        missingNotes.map(async note => {
          const links = await this.linkSvc.list(
            { id: claim.id, objectType: Link.ObjectType.CLAIM },
            { owner: { id: note.id, objectType: Link.ObjectType.NOTE } }
          );

          const linksAndConfigs = links.items.reduce(
            (acc, link) => {
              const type = Link.getLinkType(link, Link.ObjectType.NOTE);
              if (type === Link.ObjectType.ACTIVITY) return acc;

              const object = Link.getLinkObject(link, type, note.id);
              if ((object as any)?.deleted) return acc;

              const config = Link.getLinkInfoCardConfig(link, type, note.id);
              acc.push({ link, config });

              return acc;
            },
            [] as { link: Link.Model; config: InfoCardConfig }[]
          );

          this.linksByNoteId.set(note.id, linksAndConfigs);
        })
      );

      const noteAndLinks = notes.map(note => ({
        note,
        links: this.linksByNoteId.get(note.id) ?? [],
      }));

      return noteAndLinks.sort((a, b) => b.note.created.getTime() - a.note.created.getTime());
    })
  );

  openNoteDialog(note?: Note.Model) {
    if (note) this.selectedNote = note;
    else this.selectedNote = null;
    this.noteDialog?.openDialog();
    this.noteDialog?.refresh$.next(null);
  }

  openDocumentDialog(claim: Claim.Model, document: StorageItem.Model) {
    this.documentDialog?.open({ claim, document });
  }

  getItemTheme(item?: Claim.ClaimItem.Model) {
    return item ? Claim.ClaimItem.getTheme(item.status) : 'default';
  }

  async refresh(shouldRefresh = true) {
    if (!shouldRefresh) return;

    await this.claimSvc.refreshClaimNotes();
  }

  async ngOnInit(): Promise<void> {
    this.linkSvc.refreshActivity$.next(null);
    const claim = await firstValueFrom(this.claim$.pipe(take(1)));
    if (claim) await this.claimSvc.refreshClaimNotes(claim);
  }
}
